import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { tokenService } from '../../services/tokenService';
import { fetchCurrentUser, clearCurrentUser, setSiteCd } from '../CurrentUserReducer/currentUserSlice';

// ========================================
// CONSTANTS
// ========================================
export const SELECTED_SITE_STORAGE_KEY = 'selectedSiteCd';

// ========================================
// TYPES
// ========================================
export interface LoginPayload {
    email: string;
    password: string;
}

export interface LoginResponse {
    token: string;
    user?: {
        workdayId: string;
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    };
}

interface AuthState {
    loading: boolean;
    error: string | null;
    isAuthenticated: boolean;
}

const initialState: AuthState = {
    loading: false,
    error: null,
    isAuthenticated: tokenService.hasToken(), // Check if token exists on init
};

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Login user
 * Uses: POST /api/User/login
 * Stores JWT token and fetches user profile
 */
export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async (payload: LoginPayload, { dispatch, rejectWithValue }) => {
        try {
            const response: LoginResponse = await API_POST(ENDPOINTS.LOGIN, {
                email: payload.email,
                password: payload.password,
            });

            // Store the JWT token
            if (response.token) {
                tokenService.setToken(response.token);

                // Clear any stale site selection from a previous session
                localStorage.removeItem(SELECTED_SITE_STORAGE_KEY);

                // Fetch the current user profile after successful login
                const userProfile = await dispatch(fetchCurrentUser()).unwrap();
                return { ...response, isSuperUser: userProfile.isSuperUser };
            } else {
                throw new Error('No token received from server');
            }
        } catch (error: any) {
            // Clear any partial authentication state
            tokenService.removeToken();
            
            return rejectWithValue({
                message: error?.message ||
                    'Login failed. Please check your credentials.',
                isPending: error?.isPending || false
            });
        }
    }
);

/**
 * Switch site for super users
 * Uses: POST /api/User/switch-site
 * Stores new JWT token and refreshes user profile
 */
export const switchSite = createAsyncThunk(
    'auth/switchSite',
    async (payload: { siteCd: string }, { dispatch, rejectWithValue }) => {
        try {
            const response = await API_POST(ENDPOINTS.SWITCH_SITE, { siteCd: payload.siteCd });

            if (response.token) {
                tokenService.setToken(response.token);

                // Persist selected site so it survives a page refresh.
                // This is the ONLY place a super user's active site is written to storage.
                localStorage.setItem(SELECTED_SITE_STORAGE_KEY, payload.siteCd);

                await dispatch(fetchCurrentUser()).unwrap();
                // Override siteCd in Redux state with the selected site,
                // since /api/user/me returns the home site from the DB
                dispatch(setSiteCd(payload.siteCd));
            } else {
                throw new Error('No token received from server');
            }

            return response;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to switch site');
        }
    }
);

/**
 * Logout user
 * Clears JWT token and user state
 */
export const logoutUser = createAsyncThunk(
    'auth/logoutUser',
    async (_, { dispatch }) => {
        // Remove token from localStorage
        tokenService.removeToken();

        // Clear any persisted site selection
        localStorage.removeItem(SELECTED_SITE_STORAGE_KEY);

        // Clear current user state
        dispatch(clearCurrentUser());
        
        return;
    }
);

// ========================================
// SLICE
// ========================================
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearAuthError: (state) => {
            state.error = null;
        },
        setAuthenticated: (state, action) => {
            state.isAuthenticated = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                state.isAuthenticated = true;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as { message: string }).message;
                state.isAuthenticated = false;
            })
            // Logout
            .addCase(logoutUser.fulfilled, (state) => {
                state.isAuthenticated = false;
                state.error = null;
            });
    },
});

export const { clearAuthError, setAuthenticated } = authSlice.actions;
export default authSlice.reducer;